import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Button, Layout, Space, message } from 'antd';

import { useLogout } from '../../utils/hooks';
import { 
  useGetReportsQuery, 
  useDownloadReportMutation, 
  useUploadParticipationReportMutation,
  useUploadACHVoucherMutation,
} from '../../store/reports/reports.actions';

import { AppHeader } from './components/AppHeader/AppHeader';
import { FileUploadSection } from './components/FileUploadSection/FileUploadSection';
import { ReportsTable } from './components/ReportsTable/ReportsTable';
import { UploadHistoryModal } from './components/UploadHistoryModal/UploadHistoryModal';
import { useFileUpload } from './hooks/useFileUpload';
import { HistoryOutlined } from '@ant-design/icons';
const { Footer, Content } = Layout;

const AppPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLogged } = useSelector((state: RootState) => state.auth);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [uploadHistoryVisible, setUploadHistoryVisible] = useState(false);
  const [autoRefreshActive, setAutoRefreshActive] = useState(false);

  const { data: reportsResponse, isLoading: reportsLoading, refetch } = useGetReportsQuery({
    page: currentPage,
    pageSize: pageSize
  });
  
  const [downloadReport] = useDownloadReportMutation();
  const [uploadParticipationReport] = useUploadParticipationReportMutation();
  const [uploadACHVoucher] = useUploadACHVoucherMutation();
  const { logout } = useLogout();
  
  const { handleFileUpload } = useFileUpload();

  const reports = reportsResponse?.results || [];
  const paging = reportsResponse?.paging;

  useEffect(() => {
    if (!isLogged) {
      navigate('/login', { replace: true });
    }
  }, [isLogged, navigate]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (autoRefreshActive) {
      intervalId = setInterval(async () => {
        try {
          await refetch();
        } catch (error) {
        }
      }, 10000);

      setTimeout(() => {
        setAutoRefreshActive(false);
      }, 300000); 
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshActive, refetch]);

    const handleDownload = async (fileName: string) => {
    try {
      message.loading(`Downloading ${fileName}...`, 0.5);
      const result: any = await downloadReport(fileName).unwrap();
      
      let blob: Blob;
      let finalFileName = fileName; 
      if (result instanceof Blob) {
        blob = result;
      } else {
        if (result && typeof result === 'object' && result.fileContents) {
          try {
            const base64Data = result.fileContents;
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            const contentType = result.contentType || 'application/octet-stream';
            blob = new Blob([bytes], { type: contentType });
            
             if (result.fileDownloadName) {
               finalFileName = result.fileDownloadName;
             }
          } catch (decodeError) {
            blob = new Blob([JSON.stringify(result)], { type: 'application/octet-stream' });
          }
        } else if (typeof result === 'string') {
          try {
            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
            if (base64Regex.test(result)) {
              const binaryString = atob(result);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              blob = new Blob([bytes]);
            } else {
              blob = new Blob([result], { type: 'application/octet-stream' });
            }
          } catch (decodeError) {
            blob = new Blob([result], { type: 'application/octet-stream' });
          }
        } else {
          blob = new Blob([JSON.stringify(result)], { type: 'application/octet-stream' });
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      message.success(`Successfully downloaded ${finalFileName}`);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Unknown error';
      message.error(`Error downloading file ${fileName}: ${errorMessage}`);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleReloadReports = async () => {
    try {
      message.loading('Refreshing reports...', 0.5);
      await refetch();
    } catch (error: any) {
      message.error('Failed to refresh reports');
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleParticipationUpload = async (file: File) => {
    try {
      await handleFileUpload(file, uploadParticipationReport, 'Participation Report');
      setAutoRefreshActive(true);
    } catch (error) {
    }
  };

  const handleACHVoucherUpload = async (file: File) => {
    try {
      await handleFileUpload(file, uploadACHVoucher, 'ACH Voucher');
      setAutoRefreshActive(true);
    } catch (error) {
    }
  };

  const handleUploadHistory = () => {
    setUploadHistoryVisible(true);
  };

  const handleCloseUploadHistory = () => {
    setUploadHistoryVisible(false);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader onLogout={handleLogout} />
      
      <Content style={{ backgroundColor: '#FFFFFF', padding: '30px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <FileUploadSection
            onParticipationUpload={handleParticipationUpload}
            onACHVoucherUpload={handleACHVoucherUpload}
          />
          
        <Space>
          <Button 
            type="default"
            icon={<HistoryOutlined />}
            onClick={handleUploadHistory}
          >
            Upload History
          </Button>
        </Space>

          <ReportsTable
            reports={reports}
            loading={reportsLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            total={paging?.total || 0}
            onDownload={handleDownload}
            onReload={handleReloadReports}
            onTableChange={handleTableChange}
          />
        </Space>
      </Content>
      
      <Footer style={{ backgroundColor: 'aqua' }}></Footer>
      
      <UploadHistoryModal
        visible={uploadHistoryVisible}
        onClose={handleCloseUploadHistory}
      />
    </Layout>
  );
};

export default AppPage;
